import { useCallback, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Button } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';

import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import AdminPage from '../AdminPage';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpTextField from '../../form/NgpTextField';
import { DASHBOARDS_ADMIN_PRIVATE_FIELD, DASHBOARDS_ADMIN_TITLE_FIELD } from './DashboardsAdminConstants';
import { useFindById } from '../../../services/FormQueryService';
import { useErrorWrapper, useGenericError } from '../../../services/FormErrorService';
import { WARNING, createAlertConfig } from '../../../stores/AlertsStore';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { Actions } from '../../page-framework';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import NgpToggle from '../../form/NgpToggle';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { DashboardsAdminUsersSelect } from './DashboardsAdminUsersSelect';
import { DashbaordsAdminGroupsSection } from './DashboardsAdminGroupsSection';

const functionName = AdminPages.Dashboards;

const defaultFormValues = {};
const apiUrl = '/ipm/v1/admin/dashboards';
const goToUrl = '../';

export default function DashboardsAdminDetails() {
    const [groupsSelectionIsDirty, setGroupsSelectionIsDirty] = useState(false);
    const [, setShowDeleteButton] = useState(false);
    const recordId = useEntityIdParam();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const handleGenericError = useGenericError();
    const formSaveService = useFormSaveService();
    const formDeleteService = useFormDeleteService();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const methods = useForm();
    const { handleSubmit, reset, setError } = methods;

    const dashboardLinkConfig = {
        target: recordId,
    };

    const findByIdErrorHandler = useCallback(
        (errorObject) => {
            if (errorObject.getStatus && errorObject.getStatus() === 403) {
                return createAlertConfig(WARNING, t`You do not have permissions to view dashboard details.`);
            }

            handleGenericError(errorObject);
        },
        [handleGenericError]
    );

    const { initialized, finding, formData } = useFindById({
        url: `${apiUrl}/`,
        recordId,
        postFindByIdHandler: (dashboard) => {
            setShowDeleteButton(true);

            return dashboard;
        },
        resetFunc: reset,
        defaultValues: {
            ...defaultFormValues,
        },
        errorHandler: errorWrapper(findByIdErrorHandler, goToUrl),
    });

    const functionBreadCrumbs = [{ link: goToUrl, text: t`Dashboards` }];
    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, DASHBOARDS_ADMIN_TITLE_FIELD, '', {
        dashboardLinkConfig,
    });

    const handleFormSubmit = useCallback(
        (submitData) => {
            const dataToSave = {
                ...formData,
                ...submitData,
            };

            if (dataToSave?.userId === 'null_user') {
                dataToSave.userId = null;
            }

            return formSaveService.save({
                url: apiUrl,
                recordId: formData._id,
                dataToSave,
                setError,
            });
        },
        [formData, formSaveService, setError]
    );
    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);

    const dirtyCheck = useCallback(
        (isDirty) => {
            return isDirty || groupsSelectionIsDirty;
        },
        [groupsSelectionIsDirty]
    );

    const handleConfirmedDelete = useCallback(() => {
        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteById(apiUrl, recordId),
            onDeleteGoTo: goToUrl,
        })();
    }, [recordId, formDeleteService, deleteWrapper]);

    return (
        <AdminPage
            pageId={buildPageId(functionName)}
            breadcrumbs={breadcrumbs}
            actions={
                <Actions>
                    <ConfirmDeleteButton handleConfirmedDelete={handleConfirmedDelete} plural={false} />
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized || saving || deleting}
                    loading={finding}
                    isDirtyFunc={dirtyCheck}
                    showSave={false}
                    focusFirstField={false}
                >
                    <FormSection title={t`Dashboard Details`}>
                        <NgpTextField
                            fieldName={DASHBOARDS_ADMIN_TITLE_FIELD}
                            label={t`Title`}
                            required
                            inputProps={{ size: 40, maxLength: 128 }}
                        />
                        <NgpToggle fieldName={DASHBOARDS_ADMIN_PRIVATE_FIELD} label={t`Private`} />
                        <DashboardsAdminUsersSelect formData={formData} />
                    </FormSection>
                    <DashbaordsAdminGroupsSection
                        formData={formData}
                        setGroupsSelectionIsDirty={setGroupsSelectionIsDirty}
                    />
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
